import { useEffect, useRef, useState } from 'react';
import { api, API } from '@/lib/api';
import { toast } from 'sonner';
import { ChatCircleDots, Plus, PaperPlaneRight, Trash, Brain } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

const MODELS = {
  'gpt-terra': 'GPT-4o',
  'gpt-mini': 'GPT-4o mini',
};

export default function AIChat() {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null); // {chat_id, title, model, memory, messages}
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [assistantBuf, setAssistantBuf] = useState('');
  const scrollRef = useRef(null);

  const loadSessions = () => api.get('/chat/sessions').then(r => setSessions(r.data));
  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [current?.messages, assistantBuf]);

  const openSession = async (chat_id) => {
    const { data } = await api.get(`/chat/sessions/${chat_id}`);
    setCurrent(data);
    setAssistantBuf('');
  };

  const newSession = async () => {
    const { data } = await api.post('/chat/sessions', { model: 'gpt-terra', memory: true });
    await loadSessions();
    setCurrent({ ...data, messages: [] });
  };

  const deleteSession = async (chat_id) => {
    await api.delete(`/chat/sessions/${chat_id}`);
    if (current?.chat_id === chat_id) setCurrent(null);
    await loadSessions();
  };

  const updateSession = async (patch) => {
    await api.patch(`/chat/sessions/${current.chat_id}`, patch);
    setCurrent({ ...current, ...patch });
    await loadSessions();
  };

  const send = async () => {
    if (!current || !draft.trim() || streaming) return;
    const text = draft;
    setDraft('');
    // optimistic add user msg
    const userMsg = { role: 'user', content: text, created_at: new Date().toISOString(), msg_id: 'tmp' };
    setCurrent(c => ({ ...c, messages: [...(c.messages || []), userMsg] }));
    setStreaming(true);
    setAssistantBuf('');

    const token = localStorage.getItem('session_token');
    try {
      const res = await fetch(`${API}/chat/sessions/${current.chat_id}/message`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let full = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const line = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!line.startsWith('data:')) continue;
          try {
            const evt = JSON.parse(line.slice(5).trim());
            if (evt.delta) { full += evt.delta; setAssistantBuf(full); }
            if (evt.error) { toast.error(evt.error); }
            if (evt.done) { /* handled below */ }
          } catch {}
        }
      }
      // Finalize: replace optimistic + streaming buffer with server-persisted messages
      const { data } = await api.get(`/chat/sessions/${current.chat_id}`);
      setCurrent(data);
      setAssistantBuf('');
    } catch (e) {
      toast.error('Chat failed: ' + e.message);
    } finally {
      setStreaming(false);
      await loadSessions();
    }
  };

  return (
    <div className="flex h-screen">
      {/* Session list */}
      <aside className="w-72 border-r border-zinc-900 bg-zinc-950/60 p-4 flex flex-col shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono text-[10px] uppercase text-zinc-500 tracking-wider">AI Chat</div>
            <h2 className="font-display text-lg">Sessions</h2>
          </div>
          <button data-testid="new-chat-btn" onClick={newSession} className="btn-neon w-9 h-9 rounded-full flex items-center justify-center">
            <Plus size={16} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.length === 0 && <div className="text-xs font-mono text-zinc-500 p-3">No chats yet. Hit + to start.</div>}
          {sessions.map(s => (
            <div key={s.chat_id} className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-[background-color,border-color] ${
              current?.chat_id === s.chat_id ? 'bg-zinc-900 border border-zinc-800' : 'hover:bg-zinc-900/60'
            }`}>
              <button data-testid={`chat-session-${s.chat_id}`} onClick={() => openSession(s.chat_id)} className="flex-1 min-w-0 text-left">
                <div className="text-sm font-medium truncate">{s.title}</div>
                <div className="text-[10px] font-mono text-zinc-500">{MODELS[s.model]} · {s.memory ? 'memory on' : 'stateless'}</div>
              </button>
              <button data-testid={`delete-chat-${s.chat_id}`} onClick={() => deleteSession(s.chat_id)} className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition-[opacity,color]">
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <div className="flex-1 flex flex-col min-w-0">
        {!current ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <ChatCircleDots size={40} weight="duotone" className="text-[#ccff00] mx-auto mb-4" />
              <h1 className="font-display text-3xl">Your marketing co-pilot.</h1>
              <p className="text-sm text-zinc-400 mt-2">Brainstorm campaigns, song titles, hashtag packs, pitch angles.</p>
              <Button data-testid="empty-new-chat-btn" onClick={newSession} className="mt-6 btn-neon rounded-full font-display">
                <Plus size={16} className="mr-1" /> New chat
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="border-b border-zinc-900 px-6 py-4 flex items-center gap-4">
              <Input
                data-testid="chat-title-input"
                value={current.title}
                onChange={e => setCurrent({ ...current, title: e.target.value })}
                onBlur={e => updateSession({ title: e.target.value || 'Untitled' })}
                className="flex-1 bg-transparent border-0 font-display text-lg h-auto px-0 focus-visible:ring-0"
              />
              <div className="flex items-center gap-3">
                <Select value={current.model} onValueChange={v => updateSession({ model: v })}>
                  <SelectTrigger data-testid="chat-model-select" className="w-44 bg-zinc-950 border-zinc-800 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MODELS).map(([k, name]) => <SelectItem key={k} value={k}>{name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2 text-xs font-mono text-zinc-400">
                  {current.memory ? <Brain size={14} className="text-[#ccff00]" weight="duotone" /> : <Brain size={14} className="text-zinc-600" />}
                  <span>Memory</span>
                  <Switch
                    data-testid="chat-memory-toggle"
                    checked={current.memory}
                    onCheckedChange={v => updateSession({ memory: v })}
                  />
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6">
              {current.messages?.length === 0 && !streaming && (
                <div className="text-center text-sm text-zinc-500 font-mono py-12">
                  Ask anything: <em className="not-italic text-zinc-400">"Ten TikTok hooks for a lo-fi track about missing home"</em>
                </div>
              )}
              {current.messages?.map((m, i) => <Message key={m.msg_id || i} m={m} />)}
              {streaming && assistantBuf && (
                <Message m={{ role: 'assistant', content: assistantBuf + '▌' }} streaming />
              )}
              {streaming && !assistantBuf && (
                <div className="flex gap-3 items-center text-sm text-zinc-500 font-mono">
                  <div className="w-2 h-2 rounded-full bg-[#ccff00] animate-pulse" />
                  <div className="w-2 h-2 rounded-full bg-[#ccff00] animate-pulse" style={{ animationDelay: '0.2s' }} />
                  <div className="w-2 h-2 rounded-full bg-[#ccff00] animate-pulse" style={{ animationDelay: '0.4s' }} />
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-zinc-900 p-4">
              <div className="max-w-3xl mx-auto flex gap-2">
                <Input
                  data-testid="chat-input"
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Ask for angles, taglines, playlist pitches…"
                  disabled={streaming}
                  className="bg-zinc-950 border-zinc-800 h-11"
                />
                <Button data-testid="chat-send-btn" onClick={send} disabled={streaming || !draft.trim()} className="btn-neon rounded-full h-11 px-5">
                  <PaperPlaneRight size={16} weight="fill" />
                </Button>
              </div>
              <p className="max-w-3xl mx-auto mt-2 text-[10px] font-mono text-zinc-600">
                {current.memory ? 'Memory ON — full conversation history is sent each turn.' : 'Stateless — each message is independent.'}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Message({ m, streaming }) {
  const isUser = m.role === 'user';
  return (
    <div className={`flex gap-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && <div className="w-8 h-8 rounded-full border border-zinc-800 flex items-center justify-center shrink-0 mt-1"><ChatCircleDots size={14} className="text-[#ccff00]" weight="duotone" /></div>}
      <div className={`max-w-2xl rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser ? 'bg-[#ccff00] text-black rounded-tr-sm' : 'bg-zinc-900 border border-zinc-800 rounded-tl-sm'
      }`}>
        {m.content}
        {streaming && <span className="animate-pulse" />}
      </div>
    </div>
  );
}
